<script lang="ts" setup>
    import DropDownSplitButton from '../../components/DropDownSplitButton.vue';
    import DropDownButtonItem from '../../components/DropDownButtonItem.vue';
    import GeneralDialog from '../../components/GeneralDialog.vue';
    import ModalDialog from '../../components/ModalDialog.vue';
    import MessageSection from '../../components/Messages/MessageSection.vue';
    import TextInput from '../../components/TextInput.vue';
    import Icon from '../../components/Icon.vue';
    import ActionButton from "../../components/ActionButton.vue"; 
    import SearchUserGroupMultiple from '../../components/Search/SearchUserGroupMultiple.vue';
    import ChoiceTable from "../../components/ChoiceTable.vue";
    import { ICol, ValueStrDelegate, RenderModeType } from "../../lib/ChoiceTableHelpers";
    import PatientCreateQuick from "../../ChoiceScreens/Screens/PatientCreateQuick.vue";
    import { ActionButtonStyle } from '../../lib/Types/Types';

    import { computed, onMounted, ref, nextTick, watch, reactive } from 'vue';
    import { haveScreenAccess } from '../../lib/ChoiceUserConfiguration';
    import { AppScreens, MenuNameType, PdfBuilderLocation, Priority, ListNames, PrivilegeType } from '../../Enums/ChoiceEnums';
    import { MessageSectionType, DisplayStyle, TableRowType, IconTypes, LayoutOrientation } from '../../Enums/UiEnums';
    import { popAlert, confirmDialog } from '../../lib/Notification';
    import { pdfBuilder } from '../../lib/ChoiceApi';
    import { SplitItemExtended, IPdfSpitSave, IPdfSplitAuditResponse, IPdfSpitSendMessage } from '../../lib/Models/PdfSplitModel';
    import { ISearchPatientItem, ISearchResourceItem, ISearchPayerItem, ISearchDocumentFolderItem,  IListByNameExtended, ISearchPatientKey } from '../../lib/Models/SearchModel';
    import RadioButtonInput from "../../components/RadioButtonInput.vue"
    import _ from 'lodash'
    import EDocumentSaveScreen from '../EDocuments/EDocumentSaveScreen.vue'
    import Checkbox from '../../components/Inputs/Checkbox.vue'
    import ActionButtonV2 from '../../components/ActionButtonV2.vue'
    import MessageText from '../../components/Messages/MessageText.vue'
    import ChoiceButton from '../../components/Buttons/ChoiceButton.vue'

    interface IGroupItem {
        groupId : number,
        groupName : string
    }

    interface IPageImageItem {
        pageNumber : number,
        imageId? : number,
        filename? : string
    }

    interface ISearchItem {
        searchResultItem?: ISearchPatientItem | ISearchResourceItem | ISearchPayerItem,
        folderItem?: ISearchDocumentFolderItem,
        displaySearchError: boolean,
        displayFolderError: boolean
    }

    interface IBasePdfSplit {
        completing: boolean,
        filename: string,
        buttonName: string,
        pdfHeight: number,
        pageNumber: number,
        groupId: number,
        previewPdf: boolean,
        frameSource: string,
        newGroupName: string,
        splitList: SplitItemExtended[],
        selectedPage?: SplitItemExtended,
        groupList: IGroupItem[],
        selectedGroup: IGroupItem,
        previewing: boolean,
        displayPageDetails: boolean,
        displayGroupAssign: boolean,
        excludeSetting: boolean,
        createPatientPrivilege: PrivilegeType,
        saveEDocsDialogVisible?: boolean,
        sendCommunicationDialogVisible?: boolean
    }

    interface ISavePdfSplit {
        display: boolean,
        menuNameId: MenuNameType,
        filename: string,
        receivedDate: string,
        reviewList: IListByNameExtended[],
        reviewMessage: string,
        priority: Priority,
        saving: boolean,
        savingNext: boolean,
        errorMessage: string,
        imageId: number,
        createNewPatient: boolean,
        useGroupName: boolean,
        nextSave: boolean
    }

    interface IMailPdfSplit {
        display: boolean,
        mailList: IListByNameExtended[],
        subject: string,
        message: string,
        attachName: string,
        priority: Priority,
        errorMessage: string,
        mailToError: boolean,
        subjectError: boolean,
        attachNameError: boolean,
        imageId: number
    }

    interface IAuditPdfSplit {
        display: boolean,
        response?: IPdfSplitAuditResponse
    }

    const saveEDocsScreenRef = ref<InstanceType<typeof EDocumentSaveScreen>>();

    const IMAGE_ID_NOT_SAVED = 0;
    const FormatGroupColumn: ValueStrDelegate = (value: any, col: ICol, row: Record<string, any> | undefined, rowType: TableRowType): string | undefined => {
        if (rowType === TableRowType.Body)
        {
            const groupItem = base.groupList.find(o => o.groupId === row?.groupId);
            if (!groupItem)
                return "";
            return groupItem.groupName;
        }
    }

    const FormatSaveColumn: ValueStrDelegate = (value: any, col: ICol, row: Record<string, any> | undefined, rowType: TableRowType): string | undefined => {
        if (rowType === TableRowType.Body)
        {
            return row?.imageId ? "Yes" : "";
        }
    }

    const FormatCommunicationColumn: ValueStrDelegate = (value: any, col: ICol, row: Record<string, any> | undefined, rowType: TableRowType): string | undefined => {
        if (rowType === TableRowType.Body)
        {
            return row?.mailId ? "Yes" : "";
        }
    }

    const FormatExcludedColumn: ValueStrDelegate = (value: any, col: ICol, row: Record<string, any> | undefined, rowType: TableRowType): string | undefined => {
        if (rowType === TableRowType.Body)
        {
            return (row?.excluded ?? false) ? "Yes" : "";
        }
    }

    const pageDetailColumns = ref<ICol[]>([
        { Name: 'Page #', DataField: 'pageNumber' },
        { Name: 'Excluded', DataField: 'excluded', FormatValue: FormatExcludedColumn },
        { Name: 'Group Name', DataField: '', FormatValue: FormatGroupColumn },
        { Name: 'Page Saved', DataField: '', FormatValue: FormatSaveColumn },
        { Name: 'Communication Sent', DataField: '', FormatValue: FormatCommunicationColumn },
    ]);

    const groupAssignColumns = ref<ICol[]>([
        { Name: '', DataField:"selected", RenderMode: RenderModeType.Selection },
        { Name: 'Excluded', DataField: 'excluded', FormatValue: FormatExcludedColumn },
        { Name: 'Page #', DataField: 'pageNumber' },
        { Name: 'Group Name', DataField: '', FormatValue: FormatGroupColumn },
        { Name: 'Page Saved', DataField: '', FormatValue: FormatSaveColumn },
        { Name: 'Communication Sent', DataField: '', FormatValue: FormatCommunicationColumn },
    ]);

    const searchItem = ref<Record<MenuNameType, ISearchItem>>({
            [MenuNameType.Practice]: {displaySearchError: false, displayFolderError: false},
            [MenuNameType.Patient]: {displaySearchError: false, displayFolderError: false},
            [MenuNameType.Resource]: {displaySearchError: false, displayFolderError: false},
            [MenuNameType.Payer]: {displaySearchError: false, displayFolderError: false}
        });
    const newGroupDef: IGroupItem = {groupId: -1, groupName: "[New Group]"};

    const base = reactive<IBasePdfSplit>({
        completing: false, 
        filename: "", 
        buttonName: "Select Page", 
        pdfHeight: 100, 
        pageNumber: -1, 
        groupId: -1, 
        previewPdf: false, 
        frameSource: "", 
        newGroupName:"[New Group]", 
        selectedGroup: newGroupDef, 
        splitList: [], 
        groupList: [], 
        previewing: false, 
        displayPageDetails: false, 
        displayGroupAssign: false, 
        excludeSetting: false, 
        createPatientPrivilege: PrivilegeType.None
    });

    const inputGroupName = ref<HTMLInputElement>(); 
    const inputExcludePage = ref<HTMLInputElement>();
    const save = ref<ISavePdfSplit>({display: false, menuNameId: MenuNameType.Patient, filename: "", receivedDate: "", reviewList: [], reviewMessage: "", priority: Priority.Normal, saving: false, savingNext: false, errorMessage: "", imageId: IMAGE_ID_NOT_SAVED, createNewPatient: false, useGroupName: false, nextSave: false});
    const mail = ref<IMailPdfSplit>({display: false, mailList: [], subject: "", message: "", attachName: "", priority: Priority.Normal, errorMessage: "", mailToError: false, subjectError: false, attachNameError: false, imageId: IMAGE_ID_NOT_SAVED});
    const audit = ref<IAuditPdfSplit>({display: false});
    const defaultPatientKey = ref<ISearchPatientKey>();
    let eDocumentSaveKey = 0;
    
    const props = withDefaults(defineProps<{
        location: PdfBuilderLocation,
        recordId: number}>(), {
            location: PdfBuilderLocation.faxInbox
        });
        
        onMounted(async () => {
            const haveRights = await haveScreenAccess(AppScreens.PdfSplit);
            if (!haveRights) {
                await popAlert("Your user rights do not allow you to view this page.");
                return;
            }
            loadSplitFax();
            window.addEventListener("resize", windowResize);
            windowResize();
            
            setTimeout(() => { 
                onClickGroupDropdown(newGroupDef);
            }, 500);
        });
        
        // Computed
        const base64Pdf = computed<string>(() => `data:application/pdf;base64,${base.selectedPage?.base64}`);
        const preview64Pdf = computed<string>(() => `data:application/pdf;base64,${base.frameSource}`);
        
        const pageTotal = computed<number>(() => {
            if (!base.splitList)
            return 0;
            return base.splitList.length;
        });
        
        const savePageTotal = computed<number>(() => {
            if (!base.splitList)
            return 0;
            let saveList = base.splitList.filter(o => o.imageId != undefined);
            if (!saveList)
            return 0;
            return saveList.length;
        });
        const isNewGroupSelected = computed<boolean>(() => {
            return base.selectedGroup.groupId === newGroupDef.groupId;
        });
        
        const unsavedPageTotal = computed<number>(() => pageTotal.value - savePageTotal.value );
        const pageDetailsExist = computed<boolean>(() => pageTotal.value > 0 ? true : false);
        const createNewPatientEnabled = computed<boolean>(() => base.createPatientPrivilege == PrivilegeType.ReadUpdateWrite ? true : false);

        const pageGroupTotal = computed<number>(() => {
            if (!base.splitList)
            return 0;
        const groupList = base.splitList.filter(o => o.groupId != undefined);
        if (!groupList)
        return 0;
        return groupList.length;
    });

    const unGroupedTotal = computed<number>(() => pageTotal.value - pageGroupTotal.value );

    const groupTotal = computed<number>(() => {
        if (!base.groupList)
            return 0;
        return base.groupList.length; // First group is the [New Group] group
    });

    const saveGroupTotal = computed<number>(() => {
        if (!base.splitList)
            return 0;
        let saveList = base.splitList.filter(o => (o.groupId != undefined && (o.imageId != undefined || o.mailId != undefined)));
        if (!saveList)
            return 0;
        return saveList.length; 
    });

    const unsavedGroupTotal = computed<number>(() => pageGroupTotal.value - saveGroupTotal.value );

    const isPageSaved = computed<boolean>(() => {
        if (!base.splitList)
            return false;
        if (!base.selectedPage)
            return false;
        return base.selectedPage.imageId != undefined ? true : false;
    });

    const isPageSent = computed<boolean>(() => {
        if (!base.splitList)
            return false;
        if (!base.selectedPage)
            return false;
        return base.selectedPage.mailId != undefined ? true : false;
    });

    const isGroupSaved = computed<boolean>(() => {
        if (!base.splitList)
            return false;
        const groupId = base.selectedGroup.groupId;
        const savedList = base.splitList.filter(o => (o.groupId == groupId && o.imageId != undefined));
        const groupList = base.splitList.filter(o => (o.groupId == groupId));
        return (savedList.length > 0 && savedList.length == groupList.length) ? true : false;
    });

    const isGroupSavedPartially = computed<boolean>(() => {
        if (!base.splitList)
            return false;
        const groupId = base.selectedGroup.groupId;
        const savedList = base.splitList.filter(o => (o.groupId == groupId && o.imageId != undefined));
        const groupList = base.splitList.filter(o => (o.groupId == groupId));
        return (savedList.length > 0 && savedList.length != groupList.length) ? true : false;        
    });

    const isGroupSent = computed<boolean>(() => {
        if (!base.splitList)
            return false;
        const groupId = base.selectedGroup.groupId;
        const sentList = base.splitList.filter(o => (o.groupId == groupId && o.mailId != undefined));
        const groupList = base.splitList.filter(o => (o.groupId == groupId));
        return (sentList.length > 0 && sentList.length == groupList.length) ? true : false;
    });

    const isGroupSentPartially = computed<boolean>(() => {
        if (!base.splitList)
            return false;
        const groupId = base.selectedGroup.groupId;
        const sentList = base.splitList.filter(o => (o.groupId == groupId && o.mailId != undefined));
        const groupList = base.splitList.filter(o => (o.groupId == groupId));
        return (sentList.length > 0 && sentList.length != groupList.length) ? true : false;
    });

    const saveNextEnabled = computed<boolean>(() => {
        if (save.value.nextSave)
            return false;
        const groupId = base.selectedGroup.groupId;
        const saveList = base.splitList.filter(o => (o.groupId != groupId && o.groupId != undefined && o.imageId == undefined));
        return saveList.length > 0 ? true : false;
    });

    const pageLeftEnabled = computed<boolean>(() => {
        if (!base.splitList)
            return false;
        return base.pageNumber <= 1 ? false : true;
    });

    const pageRightEnabled = computed<boolean>(() => {
        if (!base.splitList)
            return false;
        return base.pageNumber >= base.splitList.length ? false : true;
    });

    const groupLeftEnabled = computed<boolean>(() => {
        if (!base.groupList)
            return false;
        return base.groupList.indexOf(base.selectedGroup) <= 0 ? false : true;
    }); 

    const groupRightEnabled = computed<boolean>(() => {
        if (!base.groupList)
            return false;
        if (isNewGroupSelected.value)
            return false;
        return base.groupList.indexOf(base.selectedGroup) >= (base.groupList.length - 1) ? false : true;
    });

    const assignEnabled = computed<boolean>(() => {
        if (!base.splitList)
            return false;
        if (isNewGroupSelected.value)
            return false;
        if (!base.selectedPage)
            return false;
        return true;
    });

    const previewEnabled = computed<boolean>(() => {
        if (!base.splitList)
            return false;
        const groupId = base.selectedGroup.groupId;
        if (groupId == newGroupDef.groupId)
            return false;
        let filterList = base.splitList.filter(o => o.groupId == groupId);
        if (filterList.length == 0)
            return false;
        return true;
    });
    const toolbarEnabled = computed<boolean>(() => {
        if (!base.splitList)
            return false;
        return base.pageNumber > 0 ? true : false;
    });

    const previewPdfDisplay = computed<string>(() => base.previewPdf ? "viewDisplay" : "viewHide");
    const pagePdfDisplay = computed<string>(() => base.previewPdf ? "viewHide" : "viewDisplay");
    const pageNumbers = computed<string>(() => {
        let pages = "";
        if (base.previewPdf) {
            if (!base.selectedGroup)
                return "";
            if (!base.splitList)
                return "";
            const groupId = base.selectedGroup.groupId;
            const filterList = base.splitList.filter(o => o.groupId == groupId);
            const pageList: string[] = [];
            for(const element of filterList){
                pageList.push(element.pageNumber.toString()); 
            }
            pages = `Group: ${base.selectedGroup.groupName}; Page(s): ${pageList.join(", ")}`;
        }
        else {
            pages = `Page: ${base.pageNumber}`;
        }
        return pages;
    });

    const previewTitle = computed<string>(() => {
        if (base.previewPdf) 
            return `Preview: ${base.selectedGroup.groupName}`;
        if (base.pageNumber < 0)
            return "";
        return `Page: ${base.pageNumber}`;
    });

    const mailSendAttachNameEnabled = computed<boolean>(() => mail.value.imageId != IMAGE_ID_NOT_SAVED);

    watch(() => save.value.useGroupName, (newValue, oldValue) => {
        setBaseFilename();
    });

    const auditExists = computed<boolean>(() => {
        if (!audit.value.response?.detailList)
            return false;
        if (audit.value.response.detailList.length > 0)
            return true;
        return false;
    });

    async function loadSplitFax() {
        const resp = await pdfBuilder.faxSplit(props.location, props.recordId);
        if (!resp)
        {
            await popAlert("There was an error trying to get the fax info");
            return;
        }
        base.filename = resp.splitInfo.filename;
        base.splitList = resp.splitList;
        base.createPatientPrivilege = resp.createPatientPrivilege;
        if (!base.splitList)
            return;
        onClickPageDropdown(1);
    }

    async function loadSplitAudit() {
        audit.value.response = await pdfBuilder.pdfSplitAudit(props.location, props.recordId);
        audit.value.display = true;
    }

    function onClickPageDropdown(data: number) {
        if (!base.splitList)
            return;
        if (data < 1)
            data = 1;
        if (data > base.splitList.length)
            data = base.splitList.length;
        base.pageNumber = data;
        base.previewPdf = false;
        base.buttonName = pageNameGroup(data-1);
        base.selectedPage = base.splitList[data-1];
        if (base.splitList[data-1].groupId){
            const group = base.groupList.find(o => o.groupId === base.splitList[data-1].groupId);
            if(group){
                onClickGroupDropdown(group);
            }
        }
        
    }

    function windowResize() {
        base.pdfHeight = window.innerHeight - (document.getElementById("pageDisplay")?.offsetTop ?? 100) - 30;
    }

    function onExcludePage() {
        if (!base.splitList)
            return;
        if (base.pageNumber < 0)
            return;
        base.splitList[base.pageNumber-1].excluded = inputExcludePage.value?.checked ?? false;
        base.splitList[base.pageNumber-1].groupId = undefined;
        if (!base.selectedPage)
            return;
        base.selectedPage.groupId = undefined;
        resetButtonName();
    }

    function onClickPage(offset : number) {
        if (!base.splitList)
            return;
        onClickPageDropdown(base.pageNumber + offset);        
    }

    function onClickGroup(offset : number) {
        if (!base.groupList)
            return;
        if (!base.selectedGroup)
            return;
        onClickGroupDropdown(base.groupList[base.groupList.indexOf(base.selectedGroup) + offset]);
    }

    function onClickGroupDropdown(group: IGroupItem) {
        if (!base.groupList)
            return;
        base.selectedGroup = group;
        base.newGroupName = base.selectedGroup.groupName;
        if(group === newGroupDef){
            nextTick(() => {
                inputGroupName.value?.select();
                inputGroupName.value?.focus();
            });
            return;
        }
    }
    function resetButtonName() {
        if (!base.selectedPage)
            return undefined;
        let index = getPageIndex(base.selectedPage?.pageNumber);
        if (index == undefined) {
            return undefined;
        }
        base.buttonName = pageNameGroup(index-1);
        return index;
    }

    function onClickAdd() {
        if (!base.selectedPage)
            return;
        if (!base.splitList)
            return;
        if (base.selectedPage?.excluded ?? false) {
            popAlert("Page has been excluded and cannot be assigned to a group", "Add Page to Group");
            return;
        }

        base.selectedPage.groupId = base.selectedGroup.groupId;
        resetButtonName();
        return;
    }

    function onClickRemove() {
        if (!base.selectedPage)
            return;
        if (!base.splitList)
            return;
        base.selectedPage.groupId = undefined;
        resetButtonName();
        return;
    }

    function onClickAssignGroup() {
        if (base.splitList) {
            for(const element of base.splitList) {
                element.selected = false;
            }
            if (base.selectedGroup) {
                let groupId = base.selectedGroup.groupId;
                let filterList = base.splitList.filter(o => o.groupId == groupId && !(o?.excluded ?? false));
                for(const element of filterList) {
                    element.selected = true;
                }
            }
        }
        base.displayGroupAssign = true;
    }

    function assignToGroup() {
        base.displayGroupAssign = false;
        if (!base.splitList)
            return;
        if (!base.selectedGroup)
            return;
        const groupId = base.selectedGroup.groupId;
        //Adds groupId to selected pages, unselected pages with the groupID become undefined, and unselected pages with another group ID untouched
        for(const element of base.splitList) {
            if (element.selected && !(element.excluded)){
                element.groupId = groupId;
            }
            else if(element.groupId === groupId){
                element.groupId = undefined;
            }
        }
        resetButtonName();
    }

    function getGroupName(groupId: number): string | undefined {
        const group = base.groupList.find(o => o.groupId === groupId);
        if(!group)
            return;
        return group.groupName;
    }

    function getPageIndex(pageNumber: number): number | undefined {
        if (!base.splitList) {
            return undefined;
        }
        return base.splitList.find(o => o.pageNumber === pageNumber)?.pageNumber;
    }
    //returns the page number of the index passed by the parameter from splitList if splitList isn't empty. If the page has an associated groupId, it concats that to the return string
    function pageNameGroup(index : number): string {
        if (!base.splitList) {
            return "";
        }
        return base.splitList[index].pageNumber + (base.splitList[index].groupId ? " (" + getGroupName(base.splitList[index].groupId as number) + ")" : "");
    }

    function onChangeSearch(searchResultItem: ISearchPatientItem | ISearchResourceItem | ISearchPayerItem, menuNameType: MenuNameType) {
        searchItem.value[menuNameType].searchResultItem = searchResultItem;
        searchItem.value[menuNameType].displaySearchError = false;
    }

    function onChangeFolder(folder: ISearchDocumentFolderItem, menuNameType: MenuNameType) {
        searchItem.value[menuNameType].folderItem = folder;
        searchItem.value[menuNameType].displayFolderError = false;
    }

    function getSendMessageImageId() : {imageId: number, filename: string} {
        let returnImageId = IMAGE_ID_NOT_SAVED;

        let arrSelectedPage: IPageImageItem[] = [];
        if (base.previewPdf) {
            if (!base.selectedGroup) {
                return {imageId: returnImageId, filename: ""};
            }
            const groupId = base.selectedGroup.groupId;
            let filterList = base.splitList.filter(o => o.groupId == groupId);
            for(const element of filterList) {
                arrSelectedPage.push({pageNumber: element.pageNumber, imageId: element.imageId, filename: element.filename});
            }
        }
        else {
            if (!base.selectedPage) {
                return {imageId: returnImageId, filename: ""};
            }
            arrSelectedPage.push({pageNumber: base.selectedPage.pageNumber, imageId: base.selectedPage.imageId, filename: base.selectedPage.filename});
        }

        // Determine if the image_id is to be passed
        let imageId = arrSelectedPage[0].imageId || IMAGE_ID_NOT_SAVED;
        let filename = arrSelectedPage[0].filename || "";
        if (imageId != IMAGE_ID_NOT_SAVED) {
            let fullList = base.splitList.filter(o => o.imageId == imageId);
            let selectedList = arrSelectedPage.filter(o => o.imageId == imageId);
            if (fullList.length == selectedList.length && selectedList.length != 0)
                returnImageId = imageId;
        }
        return {imageId: returnImageId, filename: filename};
    }
    async function onClickGroupDelete(){
        const group = base.selectedGroup;
        const confirmed = await confirmDialog(`Are you sure you want to delete: ${group.groupName}`);
        if(!confirmed){
            return;
        }
        for(const listItem of base.splitList){
            if(listItem.groupId === group.groupId){
                listItem.groupId = undefined;
            }
        }
        if (base.selectedPage){
            if(base.selectedPage.groupId === group.groupId){
                base.selectedPage.groupId = undefined;
            }
        }   
        base.groupList = base.groupList.filter(grp => grp.groupId !== group.groupId);
        onClickGroupDropdown(newGroupDef);
        base.groupList = base.groupList.map((item, index)=>{
            if(item.groupId !== newGroupDef.groupId){
                return {groupName:item.groupName,groupId:item.groupId}
            }
            return {groupName:item.groupName,groupId:newGroupDef.groupId}
        });
        resetButtonName();
    }
    async function onClickPreview() {
        await nextTick();

        if (!base.splitList) {
            return;
        }
        let post_data = {
            pageList: Array()
        };
        let groupId = base.selectedGroup.groupId;
        let filterList = base.splitList.filter(o => o.groupId == groupId);
        for(const element of filterList){
            post_data.pageList.push({pageNumber: element.pageNumber, base64: element.base64});
        }
        base.previewing = true;
        const respPreview = await pdfBuilder.pdfJoin(post_data);
        base.previewing = false;
        if (!respPreview)
        {
            await popAlert("There was an error trying to join the PDFs");
            return;
        }
        base.frameSource = respPreview.base64;
        base.previewPdf = true;
    }

    async function openEDocs() {
        if (!base.previewPdf) {
            if (base.selectedPage) {
                if ((base.selectedPage?.excluded ?? false)) {
                    const result = await confirmDialog("This page has been excluded.  Do you wish to continue?"); 
                    if (!result) {
                        return;
                    }
                }
            }
        }

        mail.value.display = false;
        save.value.display = true;
        eDocumentSaveKey += 1;
        save.value.errorMessage = "";
        save.value.imageId = IMAGE_ID_NOT_SAVED;
        setBaseFilename();
        save.value.saving = false;
    }

    function setBaseFilename() {
        save.value.filename = base.filename.replace(".pdf", "");
        if (!base.previewPdf || !save.value.useGroupName)
            return;
        if (!base.selectedGroup)
            return;
        const groupId = base.selectedGroup.groupId;
        save.value.filename = getGroupName(groupId)??".pdf";
    }

    async function saveEDocsNext() {
        save.value.nextSave = true;
        await saveEDocs();
        save.value.nextSave = false;

        setTimeout(() => { 
            saveNextGroup();
        }, 500);
    }

    async function saveNextGroup() {
        const filterList = base.splitList.filter(o => o.imageId == undefined && o.groupId != undefined);
        if (!filterList.length)
            return;
        const group = groupItemOptions.value.find(o => o.groupId === filterList[0].groupId);
        if(!group){
            return;
        }
        onClickGroupDropdown(group);
        await onClickPreview();

        openEDocs();
    }

    async function saveEDocs() {
        if (!base.splitList) {
            return;
        }

        const saveInfo = saveEDocsScreenRef.value?.getSaveInfo();
        if (!saveInfo)
            return;

        const post_data = <IPdfSpitSave>{
            location: props.location,
            recordId: props.recordId,
            imageId: save.value.imageId,
            receivedDate: saveInfo.receivedDate,
            menuId: saveInfo.folderMenuId,
            menuNameId: saveInfo.folderMenuNameId,
            entityId: saveInfo.entityId,
            filename: saveInfo.filename,
            reviewMessage: saveInfo.reviewMessage,
            reviewList: Array(),
            reviewPriority: saveInfo.priority,
            pageList: Array()
        }

        for(const element of saveInfo.reviewList ?? []) {
            post_data.reviewList?.push({identifier: element.identifier, optionalIdentifier: element.optionalIdentifier});
        }

        if (base.previewPdf) {
            if (!base.selectedGroup) {
                return;
            }
            const groupId = base.selectedGroup.groupId;
            let filterList = base.splitList.filter(o => o.groupId == groupId);
            for(const element of filterList){
                post_data.pageList.push({pageNumber: element.pageNumber, base64: element.base64});
            }
        }
        else {
            if (!base.selectedPage) {
                return;
            }
            post_data.pageList.push({pageNumber: base.selectedPage.pageNumber, base64: base.selectedPage.base64});
        }
        save.value.errorMessage = "";
        if (save.value.nextSave)
            save.value.savingNext = true;
        else
            save.value.saving = true;
        const respSave = await pdfBuilder.pdfSplitSave(post_data);
        save.value.imageId = respSave?.imageId || IMAGE_ID_NOT_SAVED;
        if (save.value.nextSave)
            save.value.savingNext = false;
        else
            save.value.saving = false;

        if (!respSave)
        {
            await popAlert("There was an error trying to save the document");
            return;
        }
        save.value.errorMessage = respSave.success ? "" : `Error: ${respSave.message}` || "";
        updateSaveResult(saveInfo.filename);
        if (respSave.success)
            save.value.display = false;
    }

    function updateSaveResult(saveFilename: string) {
        if (save.value.imageId != IMAGE_ID_NOT_SAVED) {
            if (base.previewPdf) {
                if (!base.selectedGroup) {
                    return;
                }            
                const groupId = base.selectedGroup.groupId;
                let filterList = base.splitList.filter(o => o.groupId == groupId);
                for(const element of filterList){
                    element.imageId = save.value.imageId;
                    element.filename = saveFilename;
                }
            }
            else {
                if (!base.selectedPage) {
                    return;
                }
                base.selectedPage.imageId = save.value.imageId;
                base.selectedPage.filename = saveFilename;
            }
        }
    }


    async function openMessage() {
        if (!base.previewPdf) {
            if (base.selectedPage) {
                if ((base.selectedPage?.excluded ?? false)) {
                    const result = await confirmDialog("This page has been excluded.  Do you wish to continue?"); 
                    if (!result) {
                        return;
                    }
                }
            }
        }

        save.value.display = false;
        mail.value.display = true;
        mail.value.errorMessage = "";
        const result = getSendMessageImageId();
        mail.value.imageId = result.imageId;
        mail.value.attachName = (result.imageId == IMAGE_ID_NOT_SAVED ? "" : `Doc ID #${result.imageId}; ${result.filename}`);
    }

    async function sendMessage() {
        if (!base.splitList) {
            return;
        }
        mail.value.mailToError = false;
        mail.value.subjectError = false;
        mail.value.attachNameError = false;

        let blnError = false;
        if (mail.value.mailList.length == 0) {
            mail.value.mailToError = true;
            blnError = true;
        }

        if (mail.value.subject.length == 0) {
            mail.value.subjectError = true;
            blnError = true;
        }

        if (mail.value.attachName.length == 0 && mail.value.imageId == IMAGE_ID_NOT_SAVED) {
            mail.value.attachNameError = true;
            blnError = true;
        }

        if (blnError) {
            return;
        }

        let post_data = <IPdfSpitSendMessage>{
            mailList: Array(),
            subject: mail.value.subject,
            message: mail.value.message,
            attachName: mail.value.attachName,
            priority: mail.value.priority,
            imageId: mail.value.imageId,
            pageList: Array()
        };

        for(const element of mail.value.mailList){
            post_data.mailList.push({identifier: element.identifier, optionalIdentifier: element.optionalIdentifier});
        }

        if (base.previewPdf) {
            if (!base.selectedGroup) {
                return;
            }
            let groupId = base.selectedGroup.groupId;
            let filterList = base.splitList.filter(o => o.groupId == groupId);
            for(const element of filterList){
                post_data.pageList.push({pageNumber: element.pageNumber, base64: element.base64});
            }
        }
        else {
            if (!base.selectedPage) {
                return;
            }
            post_data.pageList.push({pageNumber: base.selectedPage.pageNumber, base64: base.selectedPage.base64});
        }

        mail.value.errorMessage = "";
        const respSend = await pdfBuilder.SendMessage(post_data);//comes back as undefined and isn't handling that right
        if (!respSend)
        {
            await popAlert("There was an error sending the message");
            return;
        }
        mail.value.errorMessage = respSend.message || "";
        if (respSend.message) 
            return;
        updateMailResult(respSend.mailId);
        mail.value.display = false;
    }

    function updateMailResult(mailId?: number) {
        if (!mailId)
            return;
        if (base.previewPdf) {
            if (!base.selectedGroup) {
                return;
            }            
            let groupId = base.selectedGroup.groupId;
            let filterList = base.splitList.filter(o => o.groupId == groupId);
            for(const element of filterList){
                element.mailId = mailId;
            }
        }
        else {
            if (!base.selectedPage) {
                return;
            }
            base.selectedPage.mailId = mailId;
        }
    }

    function openAuditHistory() {
        loadSplitAudit();
    }

    async function markCompleted() {
        base.completing = true;
        await pdfBuilder.faxReceiveDelete(props.recordId);
        if (opener.location.href.indexOf("UpdoxTaskList") > 0)
            opener.finish_selected_record();
        base.completing = false;
    }

    function onSelectMailTo(selectedResults: IListByNameExtended[]) {
        mail.value.mailList = selectedResults;
    }

    function chartNumber(patientId?: number) {
        if (!patientId)
            return "";
        return `(${patientId}) `;
    }
    function receiveDate(receiveDate: string) {
        return receiveDate.length > 0 ? receiveDate : "No Receive Date";
    }
    function reviewPriority(priority?: Priority) {
        if (!priority)
            return "";
        return `${Priority[priority]}: `;
    }
    function auditPageNumber(auditDetailId: number) {
        if (!audit.value.response)
            return "";
        let pageList: string[] = [];
        for(const element of audit.value.response.pageList){
            if (element.audit_detail_id == auditDetailId) {
                pageList.push(element.page_number.toString());
            }
        }
        return pageList.join(", ");        
    }
    function auditReviewedBy(auditDetailId: number) {
        if (!audit.value.response)
            return "";
        let reviewList: string[] = [];
        for(const element of audit.value.response.reviewList){
            if (element.audit_detail_id == auditDetailId) {
                reviewList.push(element.review_name);
            }
        }
        return reviewList.join("; ");        
    }
    function pageDetails() {
        base.displayPageDetails = true;
    }

    function onPatientCreated(patientId: number) {
        save.value.createNewPatient = false;
        defaultPatientKey.value = {
            patientId: patientId
        }
    }
    const groupItemOptions = computed(()=>{
        const items = [...base.groupList];
        items.unshift(newGroupDef);
        return items;
    });
    function addNewGroup(){
        const maxGroupID = (_.maxBy(base.groupList,i=>i.groupId)?.groupId ?? 0)+1;
        const tempGroup = {groupId: maxGroupID, groupName: base.newGroupName};
        base.groupList.push(tempGroup);
        base.selectedGroup = tempGroup;
        base.newGroupName = tempGroup.groupName;
        resetButtonName();
    }
    function changeGroupName(){
        base.selectedGroup.groupName = base.newGroupName;
    }

    const showPageSplittingSidebar = computed<boolean>(() => 
        !base.saveEDocsDialogVisible && !base.sendCommunicationDialogVisible);
</script>

<style>
    .fixedPage {
        width: 310px;
    }
    .fixedPageButton {
        width: 50px;
    }
    .fixedAssignButton {
        width: 200px;
    }
    .viewDisplay {
        display: block;
    }
    .viewHide {
        display: none;
    }
    .auditLabelFixed {
        width: 50px;
    }
    .smallHeader {
        font-size: .75rem;
        font-weight: 400;
        line-height: 1rem;
    }
</style>

<template>
    <div class="row">
        <!-- Left side bar -->
        <div class="fixedPage" v-if="showPageSplittingSidebar">
            <div class="row">
                <div class="col-auto">
                    <h5>Page</h5>
                </div>
                <div v-show="isPageSaved" class="col-auto pe-0">
                    <Icon :icon="IconTypes.Save" :title="'Page has been saved individually or as part of a group.'" :displayStyle="DisplayStyle.Dark"></Icon>
                </div>
                <div v-show="isPageSent" class="col-auto ps-1 pe-0">
                    <Icon :icon="IconTypes.Send" :title="'Communication has been sent for the page.'" :displayStyle="DisplayStyle.Dark"></Icon>
                </div>
            </div>
            <div class="row mt-1">
                <div class="col">
                    <DropDownSplitButton :baseClass="'btn btn-primary label-isalus-small'">
                        <template v-slot:button>{{base.buttonName}}</template>
                        <DropDownButtonItem v-for="(file, index) in base.splitList" v-bind:data="file.pageNumber" :baseClass="'dropdown-item label-isalus-small'" v-on:click="onClickPageDropdown">
                            {{pageNameGroup(index)}}
                        </DropDownButtonItem>
                    </DropDownSplitButton>
                </div>
            </div>
            <div class="row mt-2">
                <div class="col">
                    <div class="btn-group" role="toolbar" aria-label="Page Forward / Backward">
                        <button type="button" class="btn btn-primary label-isalus-small" :disabled="!pageLeftEnabled" title="Previous Page" @click="onClickPage(-1)">
                            <Icon :icon="IconTypes.Backward" :displayStyle="DisplayStyle.Light"></Icon>
                        </button>
                        <button type="button" class="btn btn-primary label-isalus-small" :disabled="!pageRightEnabled" title="Next Page" @click="onClickPage(1)">
                            <Icon :icon="IconTypes.Forward" :displayStyle="DisplayStyle.Light"></Icon>
                        </button>
                    </div>
                </div>
                <div class="col-auto mt-1">
                    <input class="form-check-input me-1" ref="inputExcludePage" type="checkbox" :checked="base.splitList[base.pageNumber-1]?.excluded ?? false" title="Exclude Page from Assigning to Group" @input="onExcludePage()"><label class="label-isalus-small">Exclude</label>
                </div>
                <div class="col-auto">
                    <ActionButton
                        baseText="Details"
                        spinText="Details"
                        :executing="false"    
                        :disabled="false"   
                        :clickEvent="pageDetails" 
                        :toolTip="'View the page details for this session.'"
                        :executeInit="false"
                        :baseClass="'btn btn-primary label-isalus-small'"
                        :width="105">
                    </ActionButton> 
                </div>
            </div>
            <div class="row mt-2 border-bottom">
                <div class="col">
                    <p class="smallHeader">Total: {{ pageTotal }}</p>
                </div>
                <div class="col">
                    <p class="smallHeader">UnGrouped: {{ unGroupedTotal }}</p>
                </div>
                <div class="col">
                    <p class="smallHeader">UnSaved: {{ unsavedPageTotal }}</p>
                </div>
            </div>
            <div class="row mt-2">
                <div class="col-auto">
                    <div class="row">
                        <div class="col">
                            <h5>Group</h5>
                        </div>
                        <div v-show="isGroupSaved" class="col-auto pe-0">
                            <Icon :icon="IconTypes.Save" :title="'Group has been saved.'" :displayStyle="DisplayStyle.Dark"></Icon>
                        </div>
                        <div v-show="isGroupSavedPartially" class="col-auto pe-0">
                            <Icon :icon="IconTypes.Add" :title="'Pages have been added to the group after it has been saved.'" :displayStyle="DisplayStyle.Dark"></Icon>
                        </div>
                        <div v-show="isGroupSent" class="col-auto ps-1 pe-0">
                            <Icon :icon="IconTypes.Send" :title="'Communication has been sent for group.'" :displayStyle="DisplayStyle.Dark"></Icon>
                        </div>
                        <div v-show="isGroupSentPartially" class="col-auto ps-1 pe-0">
                            <Icon :icon="IconTypes.Add" :title="'Pages have been added to the group after it has been sent.'" :displayStyle="DisplayStyle.Dark"></Icon>
                        </div>
                    </div>
                </div>
                <div class="col"></div>
                <div class="col-auto">
                    <div class="btn-group" role="toolbar" aria-label="Add / Delete Button Group">
                        <button type="button" class="btn btn-primary label-isalus-small" :disabled="!assignEnabled" title="Add the page to the selected group" @click="onClickAdd()">
                            <Icon :icon="IconTypes.Add" :displayStyle="DisplayStyle.Light"></Icon>
                        </button>
                        <button type="button" class="btn btn-primary label-isalus-small" :disabled="!assignEnabled" title="Remove the page from the selected group" @click="onClickRemove()">
                            <Icon :icon="IconTypes.Delete" :displayStyle="DisplayStyle.Light"></Icon>
                        </button>
                        <button type="button" class="btn btn-primary label-isalus-small" :disabled="!assignEnabled" title="Assign/removed multiple pages to the selected group" @click="onClickAssignGroup()">
                            <Icon :icon="IconTypes.Report" :displayStyle="DisplayStyle.Light"></Icon> 
                        </button>
                    </div>
                </div>
            </div>
            <div class="row mt-2">
                <div class="col">
                    <DropDownSplitButton :baseClass="'btn btn-primary label-isalus-small'">
                        <template v-slot:button>{{isNewGroupSelected ? "Select Group" : base.selectedGroup.groupName}}</template>
                        <DropDownButtonItem v-for="group in groupItemOptions" :data="group" :baseClass="'dropdown-item label-isalus-small'" @click="onClickGroupDropdown">
                            {{group.groupName}}
                        </DropDownButtonItem>
                    </DropDownSplitButton>
                </div>
            </div>
            <div class="row mt-2">
                <div class="col">
                    <MessageSection v-if="!base.selectedGroup">No Group Selected</MessageSection>
                    <input :maxlength="32" type="text" class="form-control label-isalus-small" ref="inputGroupName" v-model="base.newGroupName" @change="()=>isNewGroupSelected ? addNewGroup() : changeGroupName()"/>
                </div>
            </div>
            <div class="row mt-3">
                <div class="col">
                    <div class="btn-group" role="toolbar" aria-label="Group Forward / Backward">
                        <button type="button" class="btn btn-primary label-isalus-small" :disabled="!groupLeftEnabled" title="Previous Group" @click="onClickGroup(-1)">
                            <Icon :icon="IconTypes.Backward" :displayStyle="DisplayStyle.Light"></Icon>
                        </button>
                        <button type="button" class="btn btn-primary label-isalus-small" :disabled="!groupRightEnabled" title="Next Group" @click="onClickGroup(1)">
                            <Icon :icon="IconTypes.Forward" :displayStyle="DisplayStyle.Light"></Icon>
                        </button>
                        <button type="button" class="btn btn-primary label-isalus-small" :title="newGroupDef.groupName" @click="onClickGroupDropdown(newGroupDef)">
                            <Icon :icon="IconTypes.New" :displayStyle="DisplayStyle.Light"></Icon>
                        </button>
                        <button type="button" class="btn btn-primary label-isalus-small" :disabled="!assignEnabled" title="Delete Current Group" @click="onClickGroupDelete()">
                            <Icon :icon="IconTypes.Delete" :displayStyle="DisplayStyle.Light"></Icon>
                        </button>
                    </div>
                </div>
                <div class="col-auto">
                    <ActionButton
                        baseText="Preview"
                        spinText="Previewing"
                        :executing="base.previewing"    
                        :disabled="!previewEnabled"   
                        :clickEvent="onClickPreview" 
                        :toolTip="'Preview the Group'"
                        :executeInit="false"
                        :width="110"
                        :baseClass="'btn btn-primary label-isalus-small'">
                    </ActionButton> 
                </div>
            </div>
            <div class="row border-bottom">
                <div class="col mt-2">
                    <p class="smallHeader">Total: {{ groupTotal }}</p>
                </div>
                <div class="col mt-2">
                </div>
                <div class="col mt-2">
                    <p class="smallHeader">UnSaved: {{ unsavedGroupTotal }}</p>
                </div>
            </div>
            <div class="row mt-2">
                <div class="col">
                    <h5>Completion</h5>
                </div>
            </div>
            <div class="row mt-2">
                <div class="col">
                    <ActionButton
                        baseText="Mark as Completed"
                        spinText="Completing"
                        :executing="base.completing"    
                        :disabled="base.completing"   
                        :clickEvent="markCompleted" 
                        :toolTip="'Mark the document as completed'"
                        :executeInit="false"
                        :width="150"
                        :baseClass="'btn btn-primary label-isalus-small'">
                    </ActionButton> 
                </div>
            </div>
        </div>

        <!-- PDF Viewer and actions -->
        <div class="col">
            <div class="row">
                <div class="col col-md-auto">
                    <div class="btn-group" role="toolbar" aria-label="PDF save/send toolbar">
                        <button type="button" class="btn btn-primary label-isalus-small" :disabled="!toolbarEnabled" title="Save the pdf to eDocuments" @click="openEDocs()">
                            <Icon :icon="IconTypes.Save" :displayStyle="DisplayStyle.Light" :endingPadding="1"></Icon>eDocs
                        </button>
                        <button type="button" class="btn btn-primary label-isalus-small" :disabled="!toolbarEnabled" title="Send the PDF as a communication" @click="openMessage()">
                            <Icon :icon="IconTypes.Send" :displayStyle="DisplayStyle.Light" :endingPadding="1"></Icon>Comm.
                        </button>
                        <button type="button" class="btn btn-primary label-isalus-small" :disabled="!toolbarEnabled" title="Display Audit History" @click="openAuditHistory()">
                            <Icon :icon="IconTypes.Report" :displayStyle="DisplayStyle.Light" :endingPadding="1"></Icon>Audit
                        </button>
                    </div>
                </div>
                <div class="col mt-2 label-isalus-small">
                    {{previewTitle}}
                </div>
                <div class="col text-end mt-2 label-isalus-small">
                    {{base.filename}}
                </div>
            </div>
            <div class="row mt-2">
                <div class="col">
                    <object id="pageDisplay" :key="base.selectedPage?.pageNumber" v-bind:data="base64Pdf" type="application/pdf" width="100%" :height="base.pdfHeight" :class="pagePdfDisplay">Page Pdf View</object>
                    <object id="previewDisplay" v-bind:data="preview64Pdf" type="application/pdf" width="100%" :height="base.pdfHeight" :class="previewPdfDisplay">Preview of the Split Group</object>
                </div>
            </div>
        </div>
    </div>

    <!-- EDocs Dialog -->
    <ModalDialog v-model:show="save.display" 
                :closeOnClickOutside="false" 
                :closeOnEscapeKey="false"
                :isModal="false"
                @hidden="() => base.saveEDocsDialogVisible = false"
                @shown="() => base.saveEDocsDialogVisible = true">
        <template #title>Save eDocuments</template>
        <EDocumentSaveScreen
            ref="saveEDocsScreenRef"
            v-model:filename="save.filename"
            filenameExt=".pdf"
            :key="eDocumentSaveKey">
            <template #FileNameRight>
                <div v-if="base.previewPdf">
                    <Checkbox v-model="save.useGroupName" title="Use Group Name for filename" class="label-isalus-small">Use Group Name</Checkbox>
                </div>
            </template>
            <template #DocumentInfo>
                {{ pageNumbers }}
            </template>            
        </EDocumentSaveScreen>
        <template #footer>
            <div v-if="save.errorMessage" class="text-danger">{{save.errorMessage}}</div>
            <ActionButtonV2 
                v-if="base.previewPdf"
                :action="ActionButtonStyle.NoIcon"
                :performAction="async () => await saveEDocsNext()"
                :disabled="!saveNextEnabled"
                :endingPadding="1"
                text="Save - Next"
                tooltip="Save the split as a PDF within eDocuments.  Continue to next unsaved Group" />

            <ActionButtonV2 
                :action="ActionButtonStyle.NoIcon"
                :performAction="async () => await saveEDocs()"
                :disabled="save.saving"   
                text="Save"
                toolTip="Save the split as a PDF within eDocuments" />
        </template>
    </ModalDialog>

    <!-- Audit Dialog -->
    <ModalDialog v-model:show="audit.display" :closeOnClickOutside=false>
        <template #title>Audit History</template>
        <div v-if="auditExists">
            <div class="row mt-2" v-for="detailItem in audit.response?.detailList">
                <div class="col border-top border-dark">
                    <div class="row">
                        <div class="col label-isalus-small" :title="detailItem.menu_name_description">{{chartNumber(detailItem.patient_id)}}{{detailItem.entity_name}}</div>                    
                        <div class="col label-isalus-small" title="Date/time the record was posted">{{detailItem.posted_date}}</div>                    
                    </div>
                    <div class="row">
                        <div class="col label-isalus-small" title="Path">{{detailItem.menu_path}}</div>                    
                        <div class="col label-isalus-small" title="Who saved the document">{{detailItem.users_id}}</div>                    
                    </div>
                    <div class="row">
                        <div class="col label-isalus-small" title="Filename">{{detailItem.filename}}</div>
                        <div class="col label-isalus-small" title="The pages that were included from the original document">Pages: {{auditPageNumber(detailItem.audit_detail_id)}}</div>
                    </div>
                    <div class="row">
                        <div class="col label-isalus-small" title="Received Date">{{receiveDate(detailItem.receive_date)}}</div>                    
                        <div class="col label-isalus-small" title="Image Identifier"><span class="text-muted">Image #</span>{{detailItem.image_id}}</div>
                    </div>
                    <div class="row" v-if="auditReviewedBy(detailItem.audit_detail_id)">
                        <div class="col label-isalus-small" title="Who the document is to be reviewed by">{{reviewPriority(detailItem.review_priority)}}<em>{{auditReviewedBy(detailItem.audit_detail_id)}}</em></div>                    
                    </div>
                    <div class="row" v-if="detailItem.review_message">
                        <div class="col label-isalus-small" title="The message associated with the review"><em>{{detailItem.review_message}}</em></div>                    
                    </div>
                </div>
            </div>
        </div>
        <div v-else>
            No Audit Information Available
        </div>
    </ModalDialog>

    <!-- Comm Dialog -->
    <ModalDialog v-model:show="mail.display" 
                :closeOnClickOutside="false" 
                :closeOnEscapeKey="false"
                :isModal="false" 
                @hidden="() => base.sendCommunicationDialogVisible = false"
                @shown="() => base.sendCommunicationDialogVisible = true">
        <template #title>Send Communication</template>
        <div class="row">
            <div class="col">
                <SearchUserGroupMultiple 
                    label="Mail To" 
                    :defaultListName="ListNames.MailFavorites"
                    :addToBaseClass="'label-isalus-small'" 
                    @onSelected="onSelectMailTo">
                </SearchUserGroupMultiple>
                <MessageSection v-if="mail.mailToError" :show-icon="false" :type="MessageSectionType.Text" :display-style="DisplayStyle.Danger">
                    Please select a User/Group to send to
                </MessageSection>      
            </div>
        </div>
        <div class="row mt-2">
            <div class="col label-isalus-small">
                Subject
            </div>
        </div>
        <div class="row">
            <div class="col">
                <TextInput 
                    v-model:text-value="mail.subject" 
                    :maxLength="200"
                    :cssFramework="'vanilla'"
                    placeholder="Please enter a subject">
                </TextInput>
                <MessageSection v-if="mail.subjectError" :show-icon="false" :type="MessageSectionType.Text" :display-style="DisplayStyle.Danger">
                    Please enter a subject
                </MessageSection>      
            </div>
        </div>
        <div class="row mt-2">
            <div class="col label-isalus-small">
                Message
            </div>
        </div>
        <div class="row">
            <div class="col">
                <TextInput 
                    v-model:text-value="mail.message" 
                    :maxLength="5000"
                    :cssFramework="'vanilla'"
                    :rows=3>
                </TextInput>
            </div>
        </div> 
        <div class="row mt-2">
            <div class="col label-isalus-small">
                Attach Name
            </div>
        </div>
        <div class="row">
            <div class="col">
                <TextInput 
                    v-model:text-value="mail.attachName" 
                    :disabled="mailSendAttachNameEnabled"
                    :maxLength="200"
                    :cssFramework="'vanilla'"
                    placeholder="Please enter an attachment name">
                </TextInput>
                <MessageSection v-if="mail.attachNameError" :show-icon="false" :type="MessageSectionType.Text" :display-style="DisplayStyle.Danger">
                    Please enter an attachment name
                </MessageSection>      
            </div>
        </div>
        <div class="row mt-2">
            <div class="col label-isalus-small">
                Priority
            </div>
        </div>
        <div class="row">
            <div class="col">
                    <RadioButtonInput :value="Priority.Low" v-model="mail.priority" :layout="LayoutOrientation.Horz">                    
                        <div class="label-isalus-small">Low</div>
                    </RadioButtonInput>
                    <RadioButtonInput :value="Priority.Normal" v-model="mail.priority" :layout="LayoutOrientation.Horz">
                        <div class="label-isalus-small">Normal</div>
                    </RadioButtonInput>
                    <RadioButtonInput :value="Priority.High" v-model="mail.priority" :layout="LayoutOrientation.Horz">
                        <div class="label-isalus-small">High</div>
                    </RadioButtonInput> 
            </div>
        </div>
        <div class="row mt-2">
            <div class="col label-isalus-small">
                {{pageNumbers}}
            </div>
        </div>        
        <template #footer>
            <div v-if="mail.errorMessage" >
                <MessageText :displayStyle="DisplayStyle.Danger">
                    mail.errorMessage: {{mail.errorMessage}}
                </MessageText>
            </div>

            <ChoiceButton 
                :action="ActionButtonStyle.NoIcon" 
                :displayStyle="DisplayStyle.Secondary"
                @click="mail.display = false" 
                text="Close" 
                :marginEnd="1" />

            <ActionButtonV2 
                :action="ActionButtonStyle.Send"
                :performAction="async () => await sendMessage()"
                :endingPadding="1"
                text="Send Message"
                spinText="Sending"
                tooltip="Send the mail message" />
        </template>
    </ModalDialog>

    <!-- Details Dialog -->
    <ModalDialog v-model:show="base.displayPageDetails" :closeOnClickOutside=false>
        <template #title>Page Details</template>
        <div v-if="pageDetailsExist">
            <p style="font-size: .75rem;">
                <ChoiceTable 
                    ref="pageDetailsTable"
                    :columns="pageDetailColumns" 
                    :data="base.splitList" 
                    :show-filters="false" 
                    :allowSorting="true"
                    :checkBoxScale="1.2"
                    :key-value-data-member="'Col3Val'">Loading...
                </ChoiceTable>
            </p> 
        </div>
        <div v-else>
            No Page Information Available
        </div>
    </ModalDialog>

    <!-- Group Assignment Dialog -->
    <ModalDialog v-model:show="base.displayGroupAssign" :closeOnClickOutside=false>
        <template #title>Group Assignment: {{ base.selectedGroup.groupName }}</template>
        <div v-if="pageDetailsExist">
            <p style="font-size: .75rem;">
                <ChoiceTable 
                    ref="groupAssignTable"
                    :columns="groupAssignColumns" 
                    :data="base.splitList" 
                    :show-filters="false" 
                    :allowSorting="true"
                    :checkBoxScale="1.2"
                    :key-value-data-member="'Col3Val'">Loading...
                </ChoiceTable>
            </p>            
        </div>
        <div v-else>
            No Page Information Available
        </div>
        <template #footer>
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
            <ActionButton
                baseText="Assign to Group"
                spinText="Assigning"
                :executing="false"    
                :disabled="false"   
                :clickEvent="assignToGroup" 
                :toolTip="'Assign the selected items to the group'"
                :executeInit="false"
                :width="150">
            </ActionButton> 
        </template>
    </ModalDialog>

    <!-- New Patient Dialog -->
    <GeneralDialog v-model:show="save.createNewPatient" :closeOnClickOutside="false">
        <template #title>Create New Patient</template>
        <PatientCreateQuick 
            :saveEnabled="1"
            :resetFields="save.createNewPatient"
            @onPatientCreated="(val) => onPatientCreated(val)">
        </PatientCreateQuick>
    </GeneralDialog>

</template>

